<template>
  <div class="storage-execution">
    <div class="page-header">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h1><i class="bi bi-box-arrow-in-down"></i> Ejecución de Almacenamiento</h1>
          <p class="page-description" v-if="storageList">
            Lista: {{ storageList.list_number }} | Transacción: {{ storageList.transaction?.reference }}
          </p>
        </div>
        <div class="header-actions">
          <button 
            class="btn btn-outline-secondary me-2" 
            @click="goBack"
          >
            <i class="bi bi-arrow-left"></i> Volver
          </button>
          <button 
            v-if="storageList && storageList.status === 'completed'"
            class="btn btn-success"
            @click="viewSummary"
          >
            <i class="bi bi-file-text"></i> Ver Resumen
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-3">Cargando lista de almacenamiento...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ error }}
    </div>

    <!-- Main Content -->
    <div v-else-if="storageList" class="storage-content">
      <!-- Progress Header -->
      <div class="progress-section mb-4">
        <div class="row">
          <div class="col-md-8">
            <div class="progress-info">
              <h5>Progreso de Almacenamiento</h5>
              <div class="progress mb-2" style="height: 25px;">
                <div 
                  class="progress-bar bg-success" 
                  :style="{ width: progressPercentage + '%' }"
                  role="progressbar"
                >
                  {{ progressPercentage }}%
                </div>
              </div>
              <small class="text-muted">
                {{ completedItems }} de {{ totalItems }} productos almacenados
              </small>
            </div>
          </div>
          <div class="col-md-4">
            <div class="status-info text-end">
              <div class="status-badge" :class="getStatusClass(storageList.status)">
                <i :class="getStatusIcon(storageList.status)"></i>
                {{ getStatusText(storageList.status) }}
              </div>
              <div class="time-info mt-2">
                <small class="text-muted">
                  <i class="bi bi-clock"></i>
                  Tiempo estimado: {{ storageList.estimated_time }} min
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Current Item Section -->
      <div v-if="currentItem && storageList.status === 'in_progress'" class="current-item-section mb-4">
        <div class="card border-primary">
          <div class="card-header bg-primary text-white">
            <h5 class="mb-0">
              <i class="bi bi-crosshair"></i>
              Producto Actual: {{ currentItem.product?.name }}
            </h5>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <div class="product-info">
                  <h6>Información del Producto</h6>
                  <p><strong>Código:</strong> {{ currentItem.product?.code }}</p>
                  <p><strong>Cantidad a almacenar:</strong> {{ currentItem.quantity_to_store }}</p>
                  <p v-if="currentItem.lot_number"><strong>Lote:</strong> {{ currentItem.lot_number }}</p>
                  <p v-if="currentItem.expiry_date"><strong>Vencimiento:</strong> {{ formatDate(currentItem.expiry_date) }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="location-info">
                  <h6>Ubicación Sugerida</h6>
                  <div class="suggested-location">
                    <div class="location-card">
                      <i class="bi bi-geo-alt-fill"></i>
                      <div>
                        <strong>{{ currentItem.suggested_location?.barcode }}</strong>
                        <br>
                        <small>{{ currentItem.suggested_location?.zone?.name }} - {{ currentItem.suggested_location?.zone?.warehouse?.name }}</small>
                      </div>
                    </div>
                  </div>
                  <div class="suggestion-reason mt-2">
                    <small class="text-muted">
                      <i class="bi bi-info-circle"></i>
                      {{ currentItem.suggestion_reason }}
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Scanning Section -->
            <div class="scanning-section mt-4">
              <div class="row">
                <!-- Location Scanning -->
                <div class="col-md-6">
                  <div class="scan-step" :class="{ 'completed': currentItem.location_scanned, 'active': !currentItem.location_scanned }">
                    <h6>
                      <i class="bi bi-1-circle-fill me-2"></i>
                      Escanear Ubicación
                    </h6>
                    <div v-if="!currentItem.location_scanned" class="location-scan-options">
                      <!-- Opción 1: Digitar código -->
                      <div class="scan-option mb-3">
                        <label class="form-label">
                          <i class="bi bi-keyboard me-2"></i>
                          Digitar código de ubicación
                        </label>
                        <div class="input-group">
                          <input 
                            type="text" 
                            class="form-control" 
                            placeholder="Ingrese el código de la ubicación"
                            v-model="locationBarcode"
                            @keyup.enter="scanLocation"
                            ref="locationInput"
                          >
                          <button 
                            class="btn btn-primary" 
                            @click="scanLocation" 
                            :disabled="!locationBarcode"
                          >
                            <i class="bi bi-check-circle me-1"></i>
                            Validar
                          </button>
                        </div>
                      </div>
                      
                      <!-- Separador -->
                      <div class="text-center mb-3">
                        <span class="text-muted">— o —</span>
                      </div>
                      
                      <!-- Opción 2: Escanear código de barras -->
                      <div class="scan-option">
                        <label class="form-label">
                          <i class="bi bi-upc-scan me-2"></i>
                          Escanear código de barras
                        </label>
                        <div class="d-grid">
                          <button 
                            class="btn btn-outline-primary btn-lg" 
                            @click="startLocationScan"
                          >
                            <i class="bi bi-upc-scan me-2"></i>
                            Escanear Código de Barras
                          </button>
                        </div>
                      </div>
                    </div>
                    <div v-else class="scan-success">
                      <i class="bi bi-check-circle-fill text-success me-2"></i>
                      Ubicación confirmada: {{ currentItem.actual_location?.barcode }}
                    </div>
                  </div>
                </div>

                <!-- Product Scanning -->
                <div class="col-md-6">
                  <div class="scan-step" :class="{ 'completed': currentItem.product_scanned, 'active': currentItem.location_scanned && !currentItem.product_scanned, 'disabled': !currentItem.location_scanned }">
                    <h6>
                      <i class="bi bi-2-circle-fill me-2"></i>
                      Escanear Producto
                      <span v-if="storageList.piece_by_piece_enabled && currentItem.location_scanned" class="badge bg-info ms-2">
                        {{ currentItem.quantity_scanned || 0 }}/{{ currentItem.quantity_to_store }}
                      </span>
                    </h6>
                    
                    <!-- Escaneo piece-by-piece -->
                    <div v-if="storageList.piece_by_piece_enabled && currentItem.location_scanned && !currentItem.product_scanned" class="product-scan-section">
                      <div class="scan-option">
                        <label class="form-label">
                          <i class="bi bi-upc-scan me-2"></i>
                          Escanear cada unidad del producto
                        </label>
                        <div class="input-group">
                          <input 
                            type="text" 
                            class="form-control" 
                            placeholder="Escanee cada unidad del producto"
                            v-model="productBarcode"
                            @keyup.enter="scanProductPiece"
                            ref="productInput"
                          >
                          <button 
                            class="btn btn-outline-success" 
                            @click="startProductScan"
                          >
                            <i class="bi bi-upc-scan me-1"></i>
                            Escanear
                          </button>
                          <button 
                            class="btn btn-success" 
                            @click="scanProductPiece" 
                            :disabled="!productBarcode"
                          >
                            <i class="bi bi-check-circle me-1"></i>
                            Validar
                          </button>
                        </div>
                      </div>
                      <div class="mt-3">
                        <div class="progress" style="height: 8px;">
                          <div 
                            class="progress-bar bg-success" 
                            :style="{ width: ((currentItem.quantity_scanned || 0) / currentItem.quantity_to_store * 100) + '%' }"
                          ></div>
                        </div>
                        <small class="text-muted mt-1 d-block">
                          <i class="bi bi-info-circle me-1"></i>
                          Progreso: {{ currentItem.quantity_scanned || 0 }} de {{ currentItem.quantity_to_store }} unidades escaneadas
                        </small>
                      </div>
                    </div>
                    
                    <!-- Escaneo tradicional -->
                    <div v-else-if="!storageList.piece_by_piece_enabled && currentItem.location_scanned && !currentItem.product_scanned" class="product-scan-section">
                      <div class="scan-option">
                        <label class="form-label">
                          <i class="bi bi-upc-scan me-2"></i>
                          Escanear código de barras del producto
                        </label>
                        <div class="input-group">
                          <input 
                            type="text" 
                            class="form-control" 
                            placeholder="Escanee el código de barras del producto"
                            v-model="productBarcode"
                            @keyup.enter="scanProduct"
                            ref="productInput"
                          >
                          <button 
                            class="btn btn-outline-success" 
                            @click="startProductScan"
                          >
                            <i class="bi bi-upc-scan me-1"></i>
                            Escanear
                          </button>
                          <button 
                            class="btn btn-success" 
                            @click="scanProduct" 
                            :disabled="!productBarcode"
                          >
                            <i class="bi bi-check-circle me-1"></i>
                            Validar
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div v-else-if="currentItem.product_scanned" class="scan-success">
                      <i class="bi bi-check-circle-fill text-success me-2"></i>
                      Producto confirmado y almacenado
                      <span v-if="storageList.piece_by_piece_enabled" class="badge bg-success ms-2">
                        {{ currentItem.quantity_scanned }}/{{ currentItem.quantity_to_store }} unidades
                      </span>
                    </div>
                    <div v-else class="scan-disabled">
                      <i class="bi bi-lock-fill text-muted me-2"></i>
                      Primero escanee la ubicación
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="action-buttons mt-4">
                <button 
                  v-if="currentItem.location_scanned && currentItem.product_scanned"
                  class="btn btn-success btn-lg"
                  @click="nextItem"
                >
                  <i class="bi bi-arrow-right"></i>
                  {{ hasNextItem ? 'Siguiente Producto' : 'Finalizar Almacenamiento' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Completion Section -->
      <div v-if="storageList.status === 'in_progress' && !currentItem" class="completion-section mb-4">
        <div class="card border-success">
          <div class="card-header bg-success text-white">
            <h5 class="mb-0">
              <i class="bi bi-check-circle"></i>
              ¡Todos los productos han sido escaneados!
            </h5>
          </div>
          <div class="card-body text-center">
            <p class="mb-4">Has completado el escaneo de todos los productos. Presiona el botón para finalizar el almacenamiento.</p>
            <button 
              class="btn btn-success btn-lg"
              @click="completeStorage"
              :disabled="completing"
            >
              <i class="bi bi-check-circle-fill"></i>
              {{ completing ? 'Finalizando...' : 'Completar Almacenamiento' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Items List -->
      <div class="items-section">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-list-check"></i>
              Lista de Productos ({{ totalItems }} items)
            </h5>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Estado</th>
                    <th>Producto</th>
                    <th>Cantidad</th>
                    <th>Ubicación Sugerida</th>
                    <th>Ubicación Real</th>
                    <th>Lote</th>
                    <th>Vencimiento</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="item in storageList.storage_items" 
                    :key="item.id"
                    :class="{ 'table-primary': item.id === currentItem?.id, 'table-success': item.status === 'stored' }"
                  >
                    <td>
                      <span class="badge" :class="getItemStatusClass(item)">
                        <i :class="getItemStatusIcon(item)"></i>
                        {{ getItemStatusText(item) }}
                      </span>
                    </td>
                    <td>
                      <strong>{{ item.product?.name }}</strong>
                      <br>
                      <small class="text-muted">{{ item.product?.code }}</small>
                    </td>
                    <td>{{ item.quantity_to_store }}</td>
                    <td>
                      <span class="location-badge">
                        {{ item.suggested_location?.barcode }}
                      </span>
                      <br>
                      <small class="text-muted">{{ item.suggested_location?.zone?.name }}</small>
                    </td>
                    <td>
                      <span v-if="item.actual_location" class="location-badge confirmed">
                        {{ item.actual_location.barcode }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>{{ item.lot_number || '-' }}</td>
                    <td>{{ item.expiry_date ? formatDate(item.expiry_date) : '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Section (for completed storage) -->
      <div v-if="storageList.status === 'completed'" class="summary-section mt-4">
        <div class="card border-success">
          <div class="card-header bg-success text-white">
            <h5 class="mb-0">
              <i class="bi bi-check-circle"></i>
              Almacenamiento Completado
            </h5>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <h6>Resumen de Ejecución</h6>
                <p><strong>Completado:</strong> {{ formatDateTime(storageList.completed_at) }}</p>
                <p><strong>Tiempo real:</strong> {{ storageList.actual_time }} minutos</p>
                <p><strong>Tiempo estimado:</strong> {{ storageList.estimated_time }} minutos</p>
                <p><strong>Eficiencia:</strong> 
                  <span :class="getEfficiencyClass()">
                    {{ getEfficiencyPercentage() }}%
                  </span>
                </p>
              </div>
              <div class="col-md-6">
                <h6>Productos Almacenados</h6>
                <p><strong>Total de items:</strong> {{ totalItems }}</p>
                <p><strong>Completados:</strong> {{ completedItems }}</p>
                <p><strong>Estrategia utilizada:</strong> {{ getStrategyText(storageList.storage_strategy) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div class="toast-container position-fixed bottom-0 end-0 p-3">
      <div 
        v-for="toast in toasts" 
        :key="toast.id"
        class="toast show"
        :class="toast.type"
        role="alert"
      >
        <div class="toast-header">
          <i :class="toast.icon" class="me-2"></i>
          <strong class="me-auto">{{ toast.title }}</strong>
          <button type="button" class="btn-close" @click="removeToast(toast.id)"></button>
        </div>
        <div class="toast-body">
          {{ toast.message }}
        </div>
      </div>
    </div>

    <!-- Modal de Escáner de Códigos de Barras -->
    <div v-if="showBarcodeScanner" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-upc-scan me-2"></i>
              Escanear Código de Barras
            </h5>
            <div v-if="currentScanningType" class="ms-auto">
              <span class="badge bg-primary">
                {{ currentScanningType === 'location' ? 'Ubicación' : 'Producto' }}
              </span>
            </div>
          </div>
          <div class="modal-body">
            <!-- Tabs para Cámara y Manual -->
            <ul class="nav nav-tabs mb-3" role="tablist">
              <li class="nav-item" role="presentation">
                <button 
                  class="nav-link" 
                  :class="{ active: scanTab === 'camera' }"
                  @click="scanTab = 'camera'"
                  :disabled="!cameraAvailable"
                >
                  <i class="bi bi-camera me-2"></i>
                  Cámara
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button 
                  class="nav-link" 
                  :class="{ active: scanTab === 'manual' }"
                  @click="scanTab = 'manual'"
                >
                  <i class="bi bi-keyboard me-2"></i>
                  Manual
                </button>
              </li>
            </ul>

            <!-- Tab Content -->
            <div class="tab-content">
              <!-- Tab de Cámara -->
              <div v-if="scanTab === 'camera'" class="tab-pane fade show active">
                <div class="text-center">
                  <video ref="videoElement" class="barcode-scanner" autoplay></video>
                  
                  <div class="mt-3">
                    <p class="text-muted">
                      Apunte la cámara hacia el código de barras
                    </p>
                    <p v-if="currentScanningType === 'location'" class="text-info">
                      <i class="bi bi-geo-alt me-1"></i>
                      Escaneando ubicación: {{ currentItem?.suggested_location?.barcode }}
                    </p>
                    <p v-if="currentScanningType === 'product'" class="text-info">
                      <i class="bi bi-box me-1"></i>
                      Escaneando producto: {{ currentItem?.product?.name }}
                    </p>
                  </div>
                </div>
              </div>
              
              <!-- Tab Manual -->
              <div v-if="scanTab === 'manual'" class="tab-pane fade show active">
                <div class="text-center">
                  <div class="input-group mb-3">
                    <input
                      v-model="manualBarcode"
                      type="text"
                      class="form-control form-control-lg"
                      placeholder="Ingrese el código de barras manualmente"
                      @keyup.enter="handleManualBarcodeScan"
                      ref="manualBarcodeInput"
                    >
                    <button
                      class="btn btn-primary"
                      @click="handleManualBarcodeScan"
                      :disabled="!manualBarcode"
                    >
                      <i class="bi bi-check-circle me-1"></i>
                      Validar
                    </button>
                  </div>
                  
                  <div class="mt-3">
                    <p class="text-muted">
                      Ingrese el código de barras manualmente
                    </p>
                    <p v-if="currentScanningType === 'location'" class="text-info">
                      <i class="bi bi-geo-alt me-1"></i>
                      Escaneando ubicación: {{ currentItem?.suggested_location?.barcode }}
                    </p>
                    <p v-if="currentScanningType === 'product'" class="text-info">
                      <i class="bi bi-box me-1"></i>
                      Escaneando producto: {{ currentItem?.product?.name }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Alertas -->
            <div v-if="scanError" class="alert alert-danger mt-3">
              <i class="bi bi-exclamation-triangle me-2"></i>
              {{ scanError }}
            </div>
            
            <div v-if="scanSuccess" class="alert alert-success mt-3">
              <i class="bi bi-check-circle me-2"></i>
              {{ scanSuccess }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="stopScanner">
              <i class="bi bi-x-circle me-1"></i>
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { BrowserMultiFormatReader } from '@zxing/library'

export default {
  name: 'StorageExecution',
  data() {
    return {
      loading: true,
      error: null,
      storageList: null,
      currentItemIndex: 0,
      locationBarcode: '',
      productBarcode: '',
      completing: false,
      toasts: [],
      toastId: 0,
      
      // Interval para actualización automática
      progressUpdateInterval: null,
      
      // Variables para escáner de códigos de barras
      showBarcodeScanner: false,
      currentScanningType: null, // 'location' o 'product'
      cameraAvailable: true,
      manualBarcode: '',
      scanTab: 'camera',
      scanError: '',
      scanSuccess: '',
      browserMultiFormatReader: null,
      
      // ========== PARSER GS1 PROFESIONAL - DATOS ==========
      
      // Separador FNC1 (ASCII 29)
      GS: String.fromCharCode(29),
      
      // Definición de AIs (Application Identifiers) según especificaciones GS1
      AI_DEFS: [
        // ---- Longitud fija ----
        { key: "00",  len: 18, fixed: true,  name: "SSCC" },
        { key: "01",  len: 14, fixed: true,  name: "GTIN" },
        { key: "02",  len: 14, fixed: true,  name: "Content GTIN" },
        { key: "11",  len: 6,  fixed: true,  name: "Prod date (YYMMDD)" },
        { key: "13",  len: 6,  fixed: true,  name: "Pack date (YYMMDD)" },
        { key: "15",  len: 6,  fixed: true,  name: "Best before (YYMMDD)" },
        { key: "17",  len: 6,  fixed: true,  name: "Expiry (YYMMDD)" },
        { key: "310", len: 6,  fixed: true,  name: "Net weight (kg, 6d)",  wildcard: true }, // 310x
        { key: "320", len: 6,  fixed: true,  name: "Net weight (lb, 6d)", wildcard: true }, // 320x
        { key: "330", len: 6,  fixed: true,  name: "Gross weight (kg, 6d)", wildcard: true }, // 330x - GS1 REQUERIDO
        { key: "340", len: 6,  fixed: true,  name: "Gross weight (lb, 6d)", wildcard: true }, // 340x - GS1 REQUERIDO
        { key: "390", len: null, fixed: false, name: "Amount w/o curr",   wildcard: true, max: 15 }, // 390x var
        { key: "414", len: 13, fixed: true,  name: "GLN" }, // Global Location Number - GS1 REQUERIDO
        
        // ---- Longitud variable ----
        { key: "10", fixed: false, name: "Batch/Lot", max: 20 },
        { key: "21", fixed: false, name: "Serial",    max: 20 },
        { key: "22", fixed: false, name: "CPV",       max: 20 }, // Consumer Product Variant
        { key: "30",  fixed: false, name: "Qty variable measure", max: 8 },
        { key: "37",  fixed: false, name: "Count", max: 8 },
        { key: "90",  fixed: false, name: "Internal use 90", max: 30 }, // GS1 REQUERIDO
        { key: "91",  fixed: false, name: "Internal use 91", max: 30 }, // GS1 REQUERIDO
        { key: "92",  fixed: false, name: "Internal use 92", max: 30 }, // GS1 REQUERIDO  
        { key: "93",  fixed: false, name: "Internal use 93", max: 30 }, // GS1 REQUERIDO
        { key: "94",  fixed: false, name: "Internal use 94", max: 30 }, // GS1 REQUERIDO
        { key: "95",  fixed: false, name: "Internal use 95", max: 30 }, // GS1 REQUERIDO
        { key: "96",  fixed: false, name: "Internal use 96", max: 30 }, // GS1 REQUERIDO
        { key: "97",  fixed: false, name: "Internal use 97", max: 30 }, // GS1 REQUERIDO
        { key: "98",  fixed: false, name: "Internal use 98", max: 30 }, // GS1 REQUERIDO
        { key: "99",  fixed: false, name: "Internal use 99", max: 30 }, // GS1 REQUERIDO
        { key: "240", fixed: false, name: "Additional ID", max: 30 },
        { key: "241", fixed: false, name: "Customer Part #", max: 30 },
        { key: "242", fixed: false, name: "MTO variant", max: 6 },
        { key: "243", fixed: false, name: "Supplier Part #", max: 30 },
        { key: "253", fixed: false, name: "GDTI + Serial", max: 30 }, // Global Document Type Identifier - GS1 REQUERIDO
        { key: "402", len: 17, fixed: true, name: "GSIN" }, // Global Shipment Identification Number - GS1 REQUERIDO
      ]
    }
  },
  computed: {
    totalItems() {
      return this.storageList?.storage_items?.length || 0
    },
    completedItems() {
      return this.storageList?.storage_items?.filter(item => item.status === 'stored').length || 0
    },
    progressPercentage() {
      if (this.totalItems === 0) return 0
      return Math.round((this.completedItems / this.totalItems) * 100)
    },
    currentItem() {
      if (!this.storageList?.storage_items) return null
      const pendingItems = this.storageList.storage_items.filter(item => item.status === 'pending')
      return pendingItems[0] || null
    },
    hasNextItem() {
      if (!this.storageList?.storage_items) return false
      const pendingItems = this.storageList.storage_items.filter(item => item.status === 'pending')
      return pendingItems.length > 1
    }
  },
  async mounted() {
    await this.loadStorageList()
    this.focusLocationInput()
    
    // Iniciar actualización automática si la lista está en progreso
    if (this.storageList && this.storageList.status === 'in_progress') {
      this.startProgressUpdateInterval()
    }
  },
  beforeUnmount() {
    // Limpiar interval al salir del componente
    this.clearProgressUpdateInterval()
    
    // Limpiar escáner de códigos de barras
    this.stopScanner()
  },
  methods: {
    // ========== PARSER GS1 PROFESIONAL - MÉTODOS ==========
    
    // Construir índices de búsqueda
    buildAIIndex() {
      const exact = new Map()
      const wild = []
      
      for (const d of this.AI_DEFS) {
        if (d.wildcard) {
          const re = new RegExp("^" + d.key + "\\d")
          wild.push({ re, def: d })
        } else {
          exact.set(d.key, d)
        }
      }
      
      return { exact, wild }
    },
    
    // Encontrar AI en posición específica
    findAI(s, pos, aiIndex) {
      const chunk4 = s.slice(pos, pos + 4)
      const chunk3 = s.slice(pos, pos + 3)
      const chunk2 = s.slice(pos, pos + 2)
      
      // Wildcards primero (310x, 320x, 390x, etc)
      for (const { re, def } of aiIndex.wild) {
        if (re.test(s.slice(pos, pos + 4))) {
          return { key: s.slice(pos, pos + 4), base: def.key, def }
        }
      }
      
      // Exactos 4 → 3 → 2 dígitos
      if (aiIndex.exact.has(chunk4)) return { key: chunk4, base: chunk4, def: aiIndex.exact.get(chunk4) }
      if (aiIndex.exact.has(chunk3)) return { key: chunk3, base: chunk3, def: aiIndex.exact.get(chunk3) }
      if (aiIndex.exact.has(chunk2)) return { key: chunk2, base: chunk2, def: aiIndex.exact.get(chunk2) }
      
      return null
    },

    // Validar si una secuencia AI es realmente válida (no un falso positivo)
    isValidAISequence(aiMatch, remainingData) {
      if (!aiMatch || !remainingData) return false
      
      const { def } = aiMatch
      
      // Para AIs de longitud fija, verificar que hay suficiente contenido
      if (def.fixed) {
        const requiredLength = def.len
        if (remainingData.length < requiredLength) {
          console.log(`❌ [StorageExecution] AI ${aiMatch.key} requiere ${requiredLength} chars, solo quedan ${remainingData.length}`)
          return false
        }
        
        // Para fechas, verificar formato básico (6 dígitos)
        if (['11', '13', '15', '17'].includes(def.key) && !/^\d{6}/.test(remainingData)) {
          console.log(`❌ [StorageExecution] AI fecha ${aiMatch.key} no tiene formato válido:`, remainingData.substring(0, 6))
          return false
        }
        
        console.log(`✅ [StorageExecution] AI fijo ${aiMatch.key} válido`)
        return true
      }
      
      // Para AIs de longitud variable
      // Verificar que hay al menos contenido mínimo razonable
      if (remainingData.length === 0) {
        console.log(`❌ [StorageExecution] AI variable ${aiMatch.key} sin contenido`)
        return false
      }
      
      // Para AI (10) lote, verificar que no es solo dígitos que parecen otro AI
      if (aiMatch.base === '10') {
        // Si el contenido restante es muy corto y podría ser confundido con otro AI, rechazar
        if (remainingData.length <= 3 && /^\d{1,3}$/.test(remainingData)) {
          console.log(`❌ [StorageExecution] AI (10) con contenido muy corto que parece AI: "${remainingData}"`)
          return false
        }
      }
      
      // Validación especial para AI (01) GTIN - debe tener exactamente 14 dígitos después
      if (aiMatch.base === '01') {
        if (remainingData.length < 14 || !/^\d{14}/.test(remainingData)) {
          console.log(`❌ [StorageExecution] AI (01) debe tener 14 dígitos, encontrado:`, remainingData.substring(0, 14))
          return false
        }
        
        // Si hay contenido después de los 14 dígitos del GTIN, verificar que sea válido
        const afterGTIN = remainingData.substring(14)
        if (afterGTIN.length > 0) {
          // Debe empezar con AI válido o ser final de código
          const nextPossibleAI = afterGTIN.substring(0, 2)
          const validNextAIs = ['10', '11', '13', '15', '17', '21', '30', '37', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99', '240', '241', '242', '243', '253', '310', '320', '330', '340', '390', '414', '402']
          
          if (!validNextAIs.includes(nextPossibleAI) && afterGTIN.length > 2) {
            console.log(`❌ [StorageExecution] AI (01) seguido de secuencia inválida: "${nextPossibleAI}"`)
            return false
          }
        }
      }
      
      console.log(`✅ [StorageExecution] AI variable ${aiMatch.key} válido`)
      return true
    },
    
    // Parser GS1 completo
    parseGS1(raw) {
      if (!raw) return { gtin: null, items: [], warnings: ["Empty input"] }
      
      // Normalización
      let s = String(raw).trim()
      s = s.replace(/^\](C1|e0|E0|d2|Q3)/i, "")   // quitar prefijos de simbología
      s = s.replace(/<FNC1>|<GS>/gi, this.GS)    // algunos lectores usan textos literales
      
      // MANEJO ESPECIAL PARA AI 01 (GTIN-14)
      if (s.startsWith('01') && s.length > 16) {
        // Para AI 01, necesitamos ser más cuidadosos
        // AI 01 siempre tiene 14 dígitos fijos
        const gtin14 = s.substring(2, 16) // Posiciones 2-15 (14 dígitos)
        const afterGTIN = s.substring(16)
        
        // Solo insertar FNC1 después del GTIN si lo que sigue parece ser un AI válido
        const nextAIPattern = /^(1[0-7]|2[0-4]|30|37|10|9[0-9]|253|310|320|330|340|390|414|402)/
        if (nextAIPattern.test(afterGTIN)) {
          s = s.substring(0, 16) + this.GS + s.substring(16)
          console.log('🔧 [GTIN SMART] Insertado FNC1 después del GTIN-14')
        }
      } else {
        // Patrones más específicos para FNC1 como '9' (solo para no-GTIN)
        s = s.replace(/(?<=\d{6})9(?=1[1-7])/g, this.GS) // Fechas seguidas de AI 11-17
        s = s.replace(/(?<=\d{3,20})9(?=1[0-7]|2[0-4]|30|37|9[0-9]|253|310|320|330|340|390|414|402)/g, this.GS) // Otros campos variables seguidos de AIs comunes
      }
      
      console.log('🔍 [StorageExecution] Parseando GS1:', s.replace(new RegExp(this.GS, 'g'), '<FNC1>'))
      
      const aiIndex = this.buildAIIndex()
      const items = []
      const warnings = []
      let gtin = null
      let i = 0
      
      while (i < s.length) {
        // Saltar separadores
        if (s[i] === this.GS) { 
          i++
          console.log('⚡ FNC1 encontrado y saltado')
          continue
        }
        
        const m = this.findAI(s, i, aiIndex)
        if (!m) {
          warnings.push(`AI desconocido en posición ${i}: "${s.slice(i, i+4)}..."`)
          console.log(`⚠️ AI desconocido en posición ${i}:`, s.slice(i, i+4))
          break
        }
        
        const { key, base, def } = m
        i += key.length
        console.log(`📊 Procesando AI ${key} (${def.name})`)
        
        let value = ""
        let ambiguous = false
        
        if (def.fixed) {
          // Longitud fija
          const need = def.len
          console.log(`🔧 [PARSER] AI ${key} (${def.name}) - Longitud fija necesaria: ${need}`)
          console.log(`🔧 [PARSER] Posición actual i: ${i}`)
          console.log(`🔧 [PARSER] String restante: "${s.slice(i)}"`)
          console.log(`🔧 [PARSER] Caracteres disponibles: ${s.length - i}`)
          
          if (i + need > s.length) {
            value = s.slice(i)
            console.log(`⚠️ [PARSER] Valor incompleto para ${key}: "${value}" (esperaba ${need} chars)`)
            warnings.push(`Valor incompleto para (${key}) esperaba ${need} chars.`)
            i = s.length
          } else {
            value = s.slice(i, i + need)
            console.log(`✅ [PARSER] Valor extraído para ${key}: "${value}" (${value.length} chars)`)
            i += need
            console.log(`🔧 [PARSER] Nueva posición i: ${i}`)
          }
        } else {
          // Longitud variable: hasta FNC1 o siguiente AI
          const max = def.max ?? 30
          let j = i
          let cutByFNC1 = false
          
          while (j < s.length && (j - i) < max) {
            if (s[j] === this.GS) { 
              cutByFNC1 = true
              break 
            }
            
            // Lógica mejorada para campos variables especialmente para AI (10) Lote
            if ((j - i) >= 3) {
              const next = this.findAI(s, j, aiIndex)
              if (next) {
                // Validación adicional para evitar falsos positivos
                const potentialValue = s.slice(i, j)
                const remainingAfterAI = s.slice(j + next.key.length)
                
                console.log(`🔍 [StorageExecution] Posible AI encontrado "${next.key}" en posición ${j}:`, {
                  currentAI: key,
                  currentValue: potentialValue,
                  nextAI: next.key,
                  remaining: remainingAfterAI.substring(0, 10) + '...'
                })
                
                // Para AI (10) Lote: ser más conservador
                if (base === '10') {
                  // Verificar si estamos cerca del final del código
                  const totalRemaining = s.length - j
                  const afterNextAI = remainingAfterAI
                  
                  console.log(`🧮 [StorageExecution] Análisis de lote:`, {
                    currentLength: j - i,
                    totalRemaining,
                    afterNextAILength: afterNextAI.length
                  })
                  
                  // Solo cortar si:
                  // 1. Ya tenemos al menos 5 caracteres Y
                  // 2. El siguiente AI tiene contenido válido después Y  
                  // 3. No estamos muy cerca del final (evitar cortes prematuros)
                  if ((j - i) >= 5 && 
                      this.isValidAISequence(next, remainingAfterAI) &&
                      afterNextAI.length >= 3) { // Debe haber contenido razonable después del AI
                    console.log(`✂️ [StorageExecution] Cortando lote en posición ${j}: "${potentialValue}"`)
                    break
                  } else {
                    console.log(`⏭️ [StorageExecution] Continuando con lote:`, {
                      reason: (j - i) < 5 ? 'muy corto' : 
                             !this.isValidAISequence(next, remainingAfterAI) ? 'AI inválido' :
                             afterNextAI.length < 3 ? 'poco contenido después' : 'otro'
                    })
                    // Continuar sin cortar
                  }
                } else {
                  // Para otros AIs, usar lógica original con validación
                  if (this.isValidAISequence(next, remainingAfterAI)) {
                    break
                  }
                }
              }
            }
            j++
          }
          
          value = s.slice(i, j)
          i = j
          if (s[i] === this.GS) i++ // Consumir FNC1 si lo había
          
          // Detectar ambigüedad
          if (!cutByFNC1) {
            const nxt = this.findAI(s, i, aiIndex)
            if (nxt && nxt.def && !nxt.def.fixed) ambiguous = true
          }
        }
        
        console.log(`✅ ${def.name} extraído:`, value, ambiguous ? '(ambiguous)' : '')
        
        // Si es GTIN (AI 01), guardarlo y limpiarlo de caracteres FNC1
        if (base === '01') {
          gtin = value.replace(/\u001d/g, '').trim()  // Limpiar carácter FNC1
          console.log(`🎯 GTIN limpio asignado:`, gtin)
        }
        
        // Limpiar el valor de caracteres FNC1 antes de agregarlo
        const cleanValue = value.replace(/\u001d/g, '').trim()
        items.push({ ai: key, aiBase: base, name: def.name, value: cleanValue, ambiguous })
      }
      
      return { gtin, items, warnings }
    },

    // Extraer GTIN de un código de barras (GS1 o simple)
    extractGTIN(barcode) {
      if (!barcode) return null
      
      console.log('🧪 [StorageExecution] ===== EXTRACTING GTIN =====')
      console.log('🧪 [StorageExecution] Input barcode:', barcode)
      console.log('🧪 [StorageExecution] Length:', barcode.length)
      console.log('🧪 [StorageExecution] Starts with 01?', barcode.startsWith('01'))
      console.log('🧪 [StorageExecution] Length > 16?', barcode.length > 16)
      
      // Si es un código GS1 (empieza con AI 01)
      if (barcode.startsWith('01') && barcode.length > 16) {
        console.log('🧪 [StorageExecution] Parsing as GS1...')
        const result = this.parseGS1(barcode)
        console.log('🧪 [StorageExecution] Parse result:', JSON.stringify(result, null, 2))
        console.log('🎯 [StorageExecution] GTIN extraído de GS1:', result.gtin)
        
        if (result.gtin) {
          return result.gtin
        } else {
          console.log('❌ [StorageExecution] No se pudo extraer GTIN, usando código completo')
          return barcode
        }
      }
      
      // Si es un código simple, devolverlo tal como está
      console.log('🧪 [StorageExecution] Tratando como código simple:', barcode)
      return barcode
    },
    
    async loadStorageList() {
      this.loading = true
      this.error = null
      
      try {
        const listId = this.$route.params.id
        const response = await api.getStorageList(listId)
        this.storageList = response.data
        
        // Si la lista no está iniciada, iniciarla automáticamente
        if (this.storageList.status === 'assigned') {
          await api.startStorageList(listId)
          this.storageList.status = 'in_progress'
        }
        
        // Iniciar actualización automática si la lista está en progreso
        if (this.storageList.status === 'in_progress') {
          this.startProgressUpdateInterval()
        }
        
      } catch (error) {
        console.error('Error loading storage list:', error)
        this.error = 'Error al cargar la lista de almacenamiento'
      } finally {
        this.loading = false
      }
    },

    async scanLocation() {
      if (!this.locationBarcode.trim()) return
      
      try {
        const response = await api.scanStorageLocation(this.currentItem.id, {
          barcode: this.locationBarcode.trim()
        })
        
        // Actualizar el item actual
        const updatedItem = response.data
        const itemIndex = this.storageList.storage_items.findIndex(item => item.id === updatedItem.id)
        if (itemIndex !== -1) {
          this.storageList.storage_items[itemIndex] = updatedItem
        }
        
        this.locationBarcode = ''
        this.showToast('success', 'Ubicación confirmada', 'La ubicación ha sido escaneada correctamente')
        
        // Focus en el input del producto
        this.$nextTick(() => {
          this.focusProductInput()
        })
        
      } catch (error) {
        console.error('Error scanning location:', error)
        this.showToast('error', 'Error', error.response?.data?.error || 'Error al escanear la ubicación')
      }
    },

    async scanProduct() {
      if (!this.productBarcode.trim()) return
      
      try {
        const originalCode = this.productBarcode.trim()
        
        console.log('🚨 [INPUT DEBUG] ===== CÓDIGO DE ENTRADA =====')
        console.log('🚨 [INPUT DEBUG] Código crudo del input:', `"${this.productBarcode}"`)
        console.log('🚨 [INPUT DEBUG] Código después de trim:', `"${originalCode}"`)
        console.log('🚨 [INPUT DEBUG] Longitud original:', originalCode.length)
        console.log('🚨 [INPUT DEBUG] Primeros 5 caracteres:', originalCode.substring(0, 5))
        console.log('🚨 [INPUT DEBUG] Últimos 5 caracteres:', originalCode.substring(originalCode.length - 5))
        console.log('🚨 [INPUT DEBUG] Código completo char por char:')
        for (let i = 0; i < Math.min(originalCode.length, 25); i++) {
          console.log(`🚨 [INPUT DEBUG] [${i}]: "${originalCode[i]}" (${originalCode.charCodeAt(i)})`)
        }
        
        // Extraer GTIN del código escaneado (GS1 o simple)
        const gtin = this.extractGTIN(originalCode)
        
        console.log('🔍 [StorageExecution] Código original:', originalCode)
        console.log('🎯 [StorageExecution] GTIN para validar:', gtin)
        
        const response = await api.scanStorageProduct(this.currentItem.id, {
          barcode: gtin || this.productBarcode.trim()
        })
        
        // Actualizar el item actual
        const updatedItem = response.data
        const itemIndex = this.storageList.storage_items.findIndex(item => item.id === updatedItem.id)
        if (itemIndex !== -1) {
          this.storageList.storage_items[itemIndex] = updatedItem
        }
        
        this.productBarcode = ''
        this.showToast('success', 'Producto almacenado', 'El producto ha sido almacenado correctamente')
        
      } catch (error) {
        console.error('Error scanning product:', error)
        
        // Si hay información detallada de comparación de códigos, mostrarla
        if (error.response?.data?.expected && error.response?.data?.scanned) {
          const errorData = error.response.data
          console.log('🚨 [STORAGE ERROR] Detalles del error de código:')
          console.log('🚨 [STORAGE ERROR] Producto:', errorData.product_name)
          console.log('🚨 [STORAGE ERROR] Código esperado (BD):', errorData.expected)
          console.log('🚨 [STORAGE ERROR] Código escaneado:', errorData.scanned)
          
          const detailedMessage = `${errorData.error}
Producto: ${errorData.product_name}
Esperado: "${errorData.expected}"
Escaneado: "${errorData.scanned}"`
          
          this.showToast('error', 'Error de Código', detailedMessage)
        } else {
          this.showToast('error', 'Error', error.response?.data?.error || 'Error al escanear el producto')
        }
      }
    },

    nextItem() {
      // El siguiente item se selecciona automáticamente por el computed currentItem
      this.$nextTick(() => {
        this.focusLocationInput()
      })
    },

    async completeStorage() {
      this.completing = true
      
      try {
        await api.completeStorageList(this.storageList.id)
        await this.loadStorageList() // Recargar para obtener el estado actualizado
        
        this.showToast('success', 'Almacenamiento completado', 'Todos los productos han sido almacenados exitosamente')
        
      } catch (error) {
        console.error('Error completing storage:', error)
        this.showToast('error', 'Error', error.response?.data?.error || 'Error al completar el almacenamiento')
      } finally {
        this.completing = false
      }
    },

    focusLocationInput() {
      this.$nextTick(() => {
        if (this.$refs.locationInput) {
          this.$refs.locationInput.focus()
        }
      })
    },

    focusProductInput() {
      this.$nextTick(() => {
        if (this.$refs.productInput) {
          this.$refs.productInput.focus()
        }
      })
    },

    goBack() {
      this.$router.push('/operator')
    },

    viewSummary() {
      // Implementar vista de resumen si es necesario
      this.showToast('info', 'Resumen', 'Funcionalidad de resumen en desarrollo')
    },

    // Status helpers
    getStatusClass(status) {
      const classes = {
        'pending': 'badge bg-warning',
        'assigned': 'badge bg-info',
        'in_progress': 'badge bg-primary',
        'completed': 'badge bg-success'
      }
      return classes[status] || 'badge bg-secondary'
    },

    getStatusIcon(status) {
      const icons = {
        'pending': 'bi bi-clock',
        'assigned': 'bi bi-person-check',
        'in_progress': 'bi bi-play-circle',
        'completed': 'bi bi-check-circle'
      }
      return icons[status] || 'bi bi-question-circle'
    },

    getStatusText(status) {
      const texts = {
        'pending': 'Pendiente',
        'assigned': 'Asignado',
        'in_progress': 'En Progreso',
        'completed': 'Completado'
      }
      return texts[status] || status
    },

    getItemStatusClass(item) {
      if (item.status === 'stored') return 'bg-success'
      if (item.location_scanned && item.product_scanned) return 'bg-success'
      if (item.location_scanned) return 'bg-warning'
      return 'bg-secondary'
    },

    getItemStatusIcon(item) {
      if (item.status === 'stored') return 'bi bi-check-circle'
      if (item.location_scanned && item.product_scanned) return 'bi bi-check-circle'
      if (item.location_scanned) return 'bi bi-clock'
      return 'bi bi-circle'
    },

    getItemStatusText(item) {
      if (item.status === 'stored') return 'Almacenado'
      if (item.location_scanned && item.product_scanned) return 'Completado'
      if (item.location_scanned) return 'Ubicación OK'
      return 'Pendiente'
    },

    getStrategyText(strategy) {
      const strategies = {
        'abc_optimized': 'ABC Optimizado',
        'chaotic_controlled': 'Caótico Controlado',
        'zone_dedicated': 'Zonas Dedicadas',
        'fifo_optimized': 'FIFO Optimizado',
        'space_optimized': 'Espacio Optimizado'
      }
      return strategies[strategy] || strategy
    },

    getEfficiencyClass() {
      const efficiency = this.getEfficiencyPercentage()
      if (efficiency >= 100) return 'text-success'
      if (efficiency >= 80) return 'text-warning'
      return 'text-danger'
    },

    getEfficiencyPercentage() {
      if (!this.storageList.estimated_time || !this.storageList.actual_time) return 100
      return Math.round((this.storageList.estimated_time / this.storageList.actual_time) * 100)
    },

    // Utility methods
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('es-ES')
    },

    formatDateTime(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('es-ES')
    },

    // Toast notifications
    showToast(type, title, message) {
      const toast = {
        id: ++this.toastId,
        type: type === 'error' ? 'text-bg-danger' : type === 'success' ? 'text-bg-success' : 'text-bg-info',
        icon: type === 'error' ? 'bi bi-exclamation-triangle' : type === 'success' ? 'bi bi-check-circle' : 'bi bi-info-circle',
        title,
        message
      }
      
      this.toasts.push(toast)
      
      // Auto remove after 5 seconds
      setTimeout(() => {
        this.removeToast(toast.id)
      }, 5000)
    },

    removeToast(id) {
      const index = this.toasts.findIndex(toast => toast.id === id)
      if (index !== -1) {
        this.toasts.splice(index, 1)
      }
    },

    startLocationScan() {
      this.currentScanningType = 'location'
      this.scanError = ''
      this.scanSuccess = ''
      this.manualBarcode = ''
      this.showBarcodeScanner = true
      this.scanTab = 'camera'
      this.cameraAvailable = true
      
      this.$nextTick(async () => {
        try {
          // Inicializar el lector de códigos de barras
          this.browserMultiFormatReader = new BrowserMultiFormatReader()
          const videoInputDevices = await this.browserMultiFormatReader.listVideoInputDevices()
          
          if (videoInputDevices.length === 0) {
            throw new Error('No se encontraron cámaras disponibles')
          }

          await this.browserMultiFormatReader.decodeFromVideoDevice(
            undefined, // Usar dispositivo por defecto
            this.$refs.videoElement,
            (result) => {
              if (result) {
                this.handleBarcodeScan(result.text)
              }
            }
          )
        } catch (error) {
          console.error('Error initializing barcode scanner:', error)
          
          // Manejar diferentes tipos de errores
          if (error.name === 'NotAllowedError') {
            this.scanError = 'Permisos de cámara denegados. Use la opción manual o permita el acceso a la cámara.'
          } else if (error.name === 'NotFoundError') {
            this.scanError = 'No se encontró ninguna cámara en el dispositivo. Use la opción manual.'
          } else if (error.name === 'NotSupportedError') {
            this.scanError = 'El navegador no soporta acceso a la cámara. Use la opción manual.'
          } else if (error.name === 'NotReadableError') {
            this.scanError = 'La cámara está siendo usada por otra aplicación. Use la opción manual.'
          } else {
            this.scanError = 'Error al acceder a la cámara. Use la opción manual: ' + (error.message || 'Error desconocido')
          }
          
          // Cambiar automáticamente al tab manual si hay error
          this.scanTab = 'manual'
          this.cameraAvailable = false
          
          // Focus en el input manual
          this.$nextTick(() => {
            if (this.$refs.manualBarcodeInput) {
              this.$refs.manualBarcodeInput.focus()
            }
          })
        }
      })
    },

    startProductScan() {
      this.currentScanningType = 'product'
      this.scanError = ''
      this.scanSuccess = ''
      this.manualBarcode = ''
      this.showBarcodeScanner = true
      this.scanTab = 'camera'
      this.cameraAvailable = true
      
      this.$nextTick(async () => {
        try {
          // Inicializar el lector de códigos de barras
          this.browserMultiFormatReader = new BrowserMultiFormatReader()
          const videoInputDevices = await this.browserMultiFormatReader.listVideoInputDevices()
          
          if (videoInputDevices.length === 0) {
            throw new Error('No se encontraron cámaras disponibles')
          }

          await this.browserMultiFormatReader.decodeFromVideoDevice(
            undefined, // Usar dispositivo por defecto
            this.$refs.videoElement,
            (result) => {
              if (result) {
                this.handleBarcodeScan(result.text)
              }
            }
          )
        } catch (error) {
          console.error('Error initializing barcode scanner:', error)
          
          // Manejar diferentes tipos de errores
          if (error.name === 'NotAllowedError') {
            this.scanError = 'Permisos de cámara denegados. Use la opción manual o permita el acceso a la cámara.'
          } else if (error.name === 'NotFoundError') {
            this.scanError = 'No se encontró ninguna cámara en el dispositivo. Use la opción manual.'
          } else if (error.name === 'NotSupportedError') {
            this.scanError = 'El navegador no soporta acceso a la cámara. Use la opción manual.'
          } else if (error.name === 'NotReadableError') {
            this.scanError = 'La cámara está siendo usada por otra aplicación. Use la opción manual.'
          } else {
            this.scanError = 'Error al acceder a la cámara. Use la opción manual: ' + (error.message || 'Error desconocido')
          }
          
          // Cambiar automáticamente al tab manual si hay error
          this.scanTab = 'manual'
          this.cameraAvailable = false
          
          // Focus en el input manual
          this.$nextTick(() => {
            if (this.$refs.manualBarcodeInput) {
              this.$refs.manualBarcodeInput.focus()
            }
          })
        }
      })
    },

    async scanProductPiece() {
      if (!this.productBarcode.trim()) return
      
      try {
        const originalCode = this.productBarcode.trim()
        
        console.log('🚨 [INPUT DEBUG PIECE] ===== CÓDIGO DE ENTRADA =====')
        console.log('🚨 [INPUT DEBUG PIECE] Código crudo del input:', `"${this.productBarcode}"`)
        console.log('🚨 [INPUT DEBUG PIECE] Código después de trim:', `"${originalCode}"`)
        console.log('🚨 [INPUT DEBUG PIECE] Longitud original:', originalCode.length)
        
        // Extraer GTIN del código escaneado (GS1 o simple)
        const gtin = this.extractGTIN(originalCode)
        
        console.log('🔍 [StorageExecution-Piece] Código original:', originalCode)
        console.log('🎯 [StorageExecution-Piece] GTIN para validar:', gtin)
        
        const response = await api.scanStorageProductPiece(this.currentItem.id, gtin || this.productBarcode.trim())
        
        if (response.data.success) {
          // Actualizar el item actual
          const updatedItem = response.data.item
          const itemIndex = this.storageList.storage_items.findIndex(item => item.id === updatedItem.id)
          if (itemIndex !== -1) {
            this.storageList.storage_items[itemIndex] = updatedItem
          }
          
          this.productBarcode = ''
          
          if (response.data.all_scanned) {
            this.showToast('success', 'Producto completado', `¡Todas las unidades de ${this.currentItem.product.name} han sido escaneadas!`)
          } else {
            this.showToast('success', 'Unidad escaneada', response.data.message)
            // Mantener el foco en el input para el siguiente escaneo
            this.$nextTick(() => {
              this.focusProductInput()
            })
          }
        }
        
      } catch (error) {
        console.error('Error scanning product piece:', error)
        
        // Si hay información detallada de comparación de códigos, mostrarla
        if (error.response?.data?.expected && error.response?.data?.scanned) {
          const errorData = error.response.data
          console.log('🚨 [STORAGE PIECE ERROR] Detalles del error de código:')
          console.log('🚨 [STORAGE PIECE ERROR] Producto:', errorData.product_name)
          console.log('🚨 [STORAGE PIECE ERROR] Código esperado (BD):', errorData.expected)
          console.log('🚨 [STORAGE PIECE ERROR] Código escaneado:', errorData.scanned)
          
          const detailedMessage = `${errorData.error}
Producto: ${errorData.product_name}
Esperado: "${errorData.expected}"
Escaneado: "${errorData.scanned}"`
          
          this.showToast('error', 'Error de Código', detailedMessage)
        } else {
          this.showToast('error', 'Error', error.response?.data?.error || 'Error al escanear el producto')
        }
      }
    },

    startProgressUpdateInterval() {
      // Limpiar interval existente si existe
      this.clearProgressUpdateInterval()
      
      // Crear nuevo interval que actualice cada 5 segundos
      this.progressUpdateInterval = setInterval(async () => {
        if (this.storageList && this.storageList.status === 'in_progress') {
          try {
            // Recargar detalles de la storage list silenciosamente
            const response = await api.getStorageList(this.storageList.id)
            this.storageList = response.data
            console.log('Progreso de almacenamiento actualizado automáticamente')
          } catch (error) {
            console.error('Error actualizando progreso automáticamente:', error)
            // No mostrar error al usuario para no interrumpir la experiencia
          }
        }
      }, 5000) // 5 segundos
    },
    
    clearProgressUpdateInterval() {
      if (this.progressUpdateInterval) {
        clearInterval(this.progressUpdateInterval)
        this.progressUpdateInterval = null
      }
    },

    handleManualBarcodeScan() {
      if (!this.manualBarcode.trim()) return
      this.handleBarcodeScan(this.manualBarcode.trim())
    },

    async handleBarcodeScan(barcode) {
      try {
        if (this.currentScanningType === 'location') {
          // Escanear ubicación
          this.locationBarcode = barcode
          await this.scanLocation()
          this.stopScanner()
        } else if (this.currentScanningType === 'product') {
          // Escanear producto
          this.productBarcode = barcode
          
          if (this.storageList.piece_by_piece_enabled) {
            console.log('🚨 [SCANNER DEBUG] ===== CÓDIGO DEL SCANNER =====')
            console.log('🚨 [SCANNER DEBUG] Código recibido del scanner:', `"${barcode}"`)
            console.log('🚨 [SCANNER DEBUG] Longitud:', barcode.length)
            console.log('🚨 [SCANNER DEBUG] Primeros 5 caracteres:', barcode.substring(0, 5))
            
            // Extraer GTIN del código escaneado (GS1 o simple)
            const gtin = this.extractGTIN(barcode)
            
            console.log('🔍 [StorageExecution-Scanner] Código original:', barcode)
            console.log('🎯 [StorageExecution-Scanner] GTIN para validar:', gtin)
            
            const response = await api.scanStorageProductPiece(this.currentItem.id, gtin || barcode)
            
            if (response.data.success) {
              // Actualizar el item actual
              const updatedItem = response.data.item
              const itemIndex = this.storageList.storage_items.findIndex(item => item.id === updatedItem.id)
              if (itemIndex !== -1) {
                this.storageList.storage_items[itemIndex] = updatedItem
              }
              
              this.scanSuccess = response.data.message
              
              if (response.data.all_scanned) {
                this.stopScanner()
                this.showToast('success', 'Producto completado', `¡Todas las unidades de ${this.currentItem.product.name} han sido escaneadas!`)
              } else {
                // Mantener el escáner abierto para el siguiente escaneo
                this.manualBarcode = ''
                this.showToast('success', 'Unidad escaneada', response.data.message)
              }
            }
          } else {
            // Escaneo tradicional
            await this.scanProduct()
            this.stopScanner()
          }
        }
        
      } catch (error) {
        console.error('Error scanning barcode:', error)
        
        // Si hay información detallada de comparación de códigos, mostrarla
        if (error.response?.data?.expected && error.response?.data?.scanned) {
          const errorData = error.response.data
          console.log('🚨 [SCANNER ERROR] Detalles del error de código:')
          console.log('🚨 [SCANNER ERROR] Producto:', errorData.product_name)
          console.log('🚨 [SCANNER ERROR] Código esperado (BD):', errorData.expected)
          console.log('🚨 [SCANNER ERROR] Código escaneado:', errorData.scanned)
          
          this.scanError = `${errorData.error} - Esperado: "${errorData.expected}", Escaneado: "${errorData.scanned}"`
        } else {
          this.scanError = error.response?.data?.error || 'Error al procesar el código de barras'
        }
      }
    },

    stopScanner() {
      try {
        // Detener el lector de códigos de barras
        if (this.browserMultiFormatReader) {
          this.browserMultiFormatReader.reset()
        }
        
        // Detener el stream de video
        if (this.$refs.videoElement && this.$refs.videoElement.srcObject) {
          const stream = this.$refs.videoElement.srcObject
          const tracks = stream.getTracks()
          tracks.forEach(track => track.stop())
          this.$refs.videoElement.srcObject = null
        }
      } catch (error) {
        console.error('Error stopping scanner:', error)
      }
      
      this.showBarcodeScanner = false
      this.currentScanningType = null
      this.scanError = ''
      this.scanSuccess = ''
      this.manualBarcode = ''
      this.scanTab = 'camera'
      this.cameraAvailable = true
    }
  },
  
  watch: {
    // Debug watcher para productBarcode
    productBarcode(newVal, oldVal) {
      console.log('🚨 [WATCHER DEBUG] ProductBarcode cambió!')
      console.log('🚨 [WATCHER DEBUG] Valor anterior:', `"${oldVal}"`)
      console.log('🚨 [WATCHER DEBUG] Valor nuevo:', `"${newVal}"`)
      console.log('🚨 [WATCHER DEBUG] Longitud nueva:', newVal ? newVal.length : 0)
      if (newVal && newVal.length > 0) {
        console.log('🚨 [WATCHER DEBUG] Primeros 5 chars:', newVal.substring(0, 5))
        console.log('🚨 [WATCHER DEBUG] Empieza con 01?:', newVal.startsWith('01'))
      }
    }
  }
}
</script>

<style scoped>
.storage-execution {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 30px;
}

.page-header h1 {
  color: #2c3e50;
  margin-bottom: 5px;
}

.page-description {
  color: #7f8c8d;
  margin: 0;
}

.loading-container {
  text-align: center;
  padding: 60px 20px;
}

.progress-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.status-badge {
  font-size: 1rem;
  padding: 8px 16px;
}

.current-item-section .card {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.product-info, .location-info {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
}

.suggested-location .location-card {
  display: flex;
  align-items: center;
  padding: 10px;
  background: white;
  border: 2px solid #007bff;
  border-radius: 6px;
  gap: 10px;
}

.suggested-location .location-card i {
  font-size: 1.5rem;
  color: #007bff;
}

.scanning-section {
  border-top: 1px solid #dee2e6;
  padding-top: 20px;
}

.scan-step {
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 15px;
  transition: all 0.3s ease;
}

.scan-step.active {
  background: #e3f2fd;
  border: 2px solid #2196f3;
}

.scan-step.completed {
  background: #e8f5e8;
  border: 2px solid #4caf50;
}

.scan-step.disabled {
  background: #f5f5f5;
  border: 2px solid #e0e0e0;
  opacity: 0.6;
}

.scan-success {
  padding: 10px;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  color: #155724;
}

.scan-disabled {
  padding: 10px;
  color: #6c757d;
}

.action-buttons {
  text-align: center;
  border-top: 1px solid #dee2e6;
  padding-top: 20px;
}

.items-section .table th {
  background: #f8f9fa;
  border-top: none;
}

.location-badge {
  background: #e9ecef;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.9rem;
}

.location-badge.confirmed {
  background: #d4edda;
  color: #155724;
}

.completion-section {
  animation: fadeIn 0.5s ease-in-out;
}

.summary-section {
  animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .storage-execution {
    padding: 15px;
  }
  
  .header-actions {
    margin-top: 15px;
  }
  
  .scan-step {
    padding: 15px;
  }
  
  .table-responsive {
    font-size: 0.9rem;
  }
}

/* Toast positioning */
.toast-container {
  z-index: 1050;
}

.toast {
  min-width: 300px;
}

/* Estilos para las opciones de escaneo de ubicación */
.location-scan-options {
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 2px solid #e9ecef;
}

.scan-option {
  padding: 15px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.scan-option .form-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.scan-option .form-label i {
  color: #007bff;
}

.scan-option .input-group .form-control {
  border-right: none;
}

.scan-option .input-group .btn {
  border-left: none;
}

.scan-option .btn-lg {
  padding: 12px 24px;
  font-weight: 500;
}

.scan-option .btn-lg i {
  font-size: 1.1rem;
}

/* Separador entre opciones */
.text-center .text-muted {
  background-color: #f8f9fa;
  padding: 0 15px;
  font-weight: 500;
}

/* Hover effects */
.scan-option:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
  transition: all 0.2s ease;
}

.scan-option .btn:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

/* Estilos para la sección de escaneo de productos */
.product-scan-section {
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 2px solid #e9ecef;
}

.product-scan-section .scan-option {
  margin-bottom: 0;
}

.product-scan-section .progress {
  border-radius: 4px;
  background-color: #e9ecef;
}

.product-scan-section .progress-bar {
  border-radius: 4px;
}

/* Mejorar la apariencia de los botones en grupos */
.input-group .btn {
  border-radius: 0;
}

.input-group .btn:last-child {
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

.input-group .btn:not(:last-child) {
  border-right: 1px solid rgba(255, 255, 255, 0.2);
}

/* Estilos para el modal del escáner */
.barcode-scanner {
  width: 100%;
  max-width: 400px;
  height: 300px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
}

.modal-dialog {
  max-width: 600px;
}

.nav-tabs .nav-link {
  border: 1px solid transparent;
  border-top-left-radius: 0.375rem;
  border-top-right-radius: 0.375rem;
}

.nav-tabs .nav-link.active {
  color: #495057;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
}

.nav-tabs .nav-link:disabled {
  color: #6c757d;
  background-color: transparent;
  border-color: transparent;
}

.tab-content {
  border: 1px solid #dee2e6;
  border-top: none;
  padding: 20px;
  border-radius: 0 0 0.375rem 0.375rem;
}

.input-group .form-control-lg {
  font-size: 1.1rem;
  padding: 0.75rem 1rem;
}

.alert {
  border-radius: 0.375rem;
}

.modal-content {
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}
</style> 